import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import './CommissionManagement.css';

interface OperatorCommission {
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  commissionPercentage: number;
}

interface UserCommission {
  userId: number;
  username: string;
  fullName: string;
  operatorCommissions: OperatorCommission[];
}

interface UpdateUserCommissionRequest {
  userId: number;
  operatorCommissions: {
    operatorId: number;
    commissionPercentage: number;
  }[];
}

const CommissionManagement: React.FC = () => {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [editingCommissions, setEditingCommissions] = useState<Record<number, number>>({});
  const [isEditing, setIsEditing] = useState(false);

  const queryClient = useQueryClient();

  // Fetch all user commissions
  const { data: allUserCommissions, isLoading: isLoadingUsers } = useQuery<UserCommission[]>({
    queryKey: ['all-user-commissions'],
    queryFn: async () => {
      const response = await axios.get('/admin/commissions/users');
      return response.data;
    }
  });

  // Fetch specific user commissions
  const { data: selectedUserCommissions, isLoading: isLoadingUserCommissions } = useQuery<UserCommission>({
    queryKey: ['user-commissions', selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return null;
      const response = await axios.get(`/admin/commissions/users/${selectedUserId}`);
      return response.data;
    },
    enabled: !!selectedUserId
  });

  // Update user commissions mutation
  const updateCommissionsMutation = useMutation({
    mutationFn: async (request: UpdateUserCommissionRequest) => {
      const response = await axios.put('/admin/commissions/users', request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-commissions', selectedUserId] });
      queryClient.invalidateQueries({ queryKey: ['all-user-commissions'] });
      setIsEditing(false);
      setEditingCommissions({});
      alert('User commissions updated successfully!');
    },
    onError: (error: any) => {
      console.error('Error updating commissions:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update commissions';
      alert(`Error: ${errorMessage}`);
    }
  });

  const handleUserSelect = (userId: number) => {
    setSelectedUserId(userId);
    setIsEditing(false);
    setEditingCommissions({});
  };

  const handleEditClick = () => {
    if (!selectedUserCommissions) return;
    
    setIsEditing(true);
    const initialCommissions: Record<number, number> = {};
    selectedUserCommissions.operatorCommissions.forEach(oc => {
      initialCommissions[oc.operatorId] = oc.commissionPercentage * 100; // Convert to percentage
    });
    setEditingCommissions(initialCommissions);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingCommissions({});
  };

  const handleSaveCommissions = () => {
    if (!selectedUserId || !selectedUserCommissions) return;

    const operatorCommissions = Object.entries(editingCommissions).map(([operatorId, percentage]) => ({
      operatorId: parseInt(operatorId),
      commissionPercentage: percentage / 100 // Convert percentage to decimal
    }));

    updateCommissionsMutation.mutate({
      userId: selectedUserId,
      operatorCommissions
    });
  };

  const handleCommissionChange = (operatorId: number, value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setEditingCommissions(prev => ({
        ...prev,
        [operatorId]: parseFloat(value) || 0
      }));
    }
  };

  const validateCommissions = () => {
    return Object.values(editingCommissions).every(rate => rate >= 0 && rate <= 100);
  };

  if (isLoadingUsers) {
    return (
      <div className="commission-management">
        <div className="loading">Loading user commissions...</div>
      </div>
    );
  }

  return (
    <div className="commission-management">
      <div className="header">
        <h1>Commission Management</h1>
        <p>Manage individual user commission rates per operator</p>
      </div>

      <div className="management-container">
        {/* User Selection Panel */}
        <div className="user-selection-panel">
          <h2>Select User</h2>
          <div className="user-list">
            {allUserCommissions?.map((user) => (
              <div
                key={user.userId}
                className={`user-item ${selectedUserId === user.userId ? 'selected' : ''}`}
                onClick={() => handleUserSelect(user.userId)}
              >
                <div className="user-info">
                  <div className="user-name">{user.fullName}</div>
                  <div className="username">@{user.username}</div>
                </div>
                <div className="commission-count">
                  {user.operatorCommissions.length} operators
                </div>
              </div>
            ))}
          </div>

          {allUserCommissions?.length === 0 && (
            <div className="no-users">
              <p>No users found</p>
            </div>
          )}
        </div>

        {/* Commission Details Panel */}
        <div className="commission-details-panel">
          {selectedUserId ? (
            <>
              <div className="details-header">
                <div className="user-details">
                  <h2>{selectedUserCommissions?.fullName}</h2>
                  <p>@{selectedUserCommissions?.username}</p>
                </div>
                <div className="actions">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleSaveCommissions}
                        className="save-btn"
                        disabled={updateCommissionsMutation.isPending || !validateCommissions()}
                      >
                        {updateCommissionsMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="cancel-btn"
                        disabled={updateCommissionsMutation.isPending}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button onClick={handleEditClick} className="edit-btn">
                      Edit Commissions
                    </button>
                  )}
                </div>
              </div>

              {isLoadingUserCommissions ? (
                <div className="loading">Loading user details...</div>
              ) : (
                <div className="commissions-table-container">
                  <table className="commissions-table">
                    <thead>
                      <tr>
                        <th>Operator</th>
                        <th>Code</th>
                        <th>Commission Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedUserCommissions?.operatorCommissions.map((commission) => (
                        <tr key={commission.operatorId}>
                          <td>
                            <span className="operator-name">{commission.operatorName}</span>
                          </td>
                          <td>
                            <span className="operator-code">{commission.operatorCode}</span>
                          </td>
                          <td>
                            {isEditing ? (
                              <div className="rate-edit">
                                <input
                                  type="text"
                                  value={editingCommissions[commission.operatorId] || 0}
                                  onChange={(e) => handleCommissionChange(commission.operatorId, e.target.value)}
                                  className="rate-input"
                                  placeholder="5.0"
                                />
                                <span className="rate-suffix">%</span>
                              </div>
                            ) : (
                              <span className="rate-display">
                                {(commission.commissionPercentage * 100).toFixed(2)}%
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {selectedUserCommissions?.operatorCommissions.length === 0 && (
                    <div className="no-commissions">
                      <p>No operator commissions found for this user</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="no-selection">
              <div className="no-selection-content">
                <h3>Select a User</h3>
                <p>Choose a user from the list to view and edit their commission rates</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="info-section">
        <div className="info-box">
          <h3>Commission Management Guide</h3>
          <ul>
            <li>Select a user from the left panel to view their commission rates</li>
            <li>Click "Edit Commissions" to modify rates for individual operators</li>
            <li>Commission rates are entered as percentages (e.g., 5.0 for 5%)</li>
            <li>Changes apply immediately after saving</li>
            <li>Users will see their updated rates on their dashboard</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CommissionManagement;
